// src/pages/Admin/RoleComponents/RoleModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import FeedbackModal from "../../PermissionManagement/PermissionComponents/FeedbackModal";
import { getRoleDropdowns } from "../../../../utils/api";
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const RoleModal = ({
  isModalOpen,
  isEditMode,
  currentRole,
  setCurrentRole,
  handleFormSubmit,
  handleCloseModal,
  handleFailure,
}) => {
  const [roleTypes, setRoleTypes] = useState([]);
  const [applicableOn, setApplicableOn] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({ open: false, title: "", message: "", type: "success" });

  useEffect(() => {
    if (isModalOpen) {
      const fetchDropdowns = async () => {
        setIsLoading(true);
        try {
          // Use the new API utility function
          const response = await getRoleDropdowns();
          console.log(response);
          if (response.data.code === "0000") {
            setRoleTypes(response.data.data.roleTypes);
            setApplicableOn(response.data.data.applicableOn);
          } else {
            // Handle API-specific errors, similar to your other components
            handleFailure("fetch dropdowns", { response: { data: { msg: response.msg } } });
          }
        } catch (err) {
          console.error("Error fetching dropdowns:", err);
          // Handle general fetch errors
          handleFailure("fetch dropdowns", err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchDropdowns();
    }
  }, [isModalOpen]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Handle the name key for existing roles and roleName for new roles
    const key = name === "roleName" && isEditMode ? "name" : name;
    setCurrentRole((prev) => ({ ...prev, [key]: value }));
  };

  // Determine if the form can be submitted
  const canSubmit = isEditMode
    ? !!currentRole.description // In edit mode, only description can change
    : !!currentRole.roleName && !!currentRole.description && !!currentRole.type && !!currentRole.applicableOn;

  return (
    <>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={modalStyle}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleFormSubmit();
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {isEditMode ? "Edit Role" : "Create New Role"}
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Role Name"
            name="roleName"
            // Updated value to handle both new and existing roles
            value={currentRole.roleName || currentRole.name || ""}
            onChange={handleChange}
            disabled={isEditMode} // Role name cannot be changed in edit mode
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Description"
            name="description"
            value={currentRole.description || ""}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="normal" required disabled={isEditMode || isLoading}>
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={currentRole.type || ""}
              label="Type"
              onChange={handleChange}
              disabled={isEditMode}
            >
              {isLoading ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                roleTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required disabled={isEditMode || isLoading}>
            <InputLabel>Applicable On</InputLabel>
            <Select
              name="applicableOn"
              value={currentRole.applicableOn || ""}
              label="Applicable On"
              onChange={handleChange}
              disabled={isEditMode}
            >
              {isLoading ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                applicableOn.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                color: "#ED141F",
                borderColor: "#ED141F",
                "&:hover": {
                  borderColor: "#ED141F",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              sx={{
                backgroundColor: "#ED141F",
                "&:hover": {
                  backgroundColor: "#C31018",
                },
              }}
            >
              {isEditMode ? "Save Changes" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>
      <FeedbackModal
        open={feedbackModal.open}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={() => setFeedbackModal({ ...feedbackModal, open: false })}
        type={feedbackModal.type}
      />
    </>
  );
};

export default RoleModal;
